import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  DateTimePicker,
  IconButton,
  Icons,
  Modal,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { useDispatch } from 'react-redux';
import updateLti1p3Assignment from '@core/api/lti/updateLti1p3Assignment';
import showToast from '@core/toasts/operations/showToast.operation';
import { Assignment } from '@core/assignments/types';
import { Space } from '@core/types/state';
import { AssignmentLaunchDetails } from '@ublend-npm/aula-schema';
import AssignmentSuccessView from '../CreateAssignment/AssignmentSuccessView';
import { createStyles } from '../CreateAssignment/CreateAssignmentModal.styles';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  assignment?: Assignment;
  currentSpace: Space;
};

type DateFieldKey = 'openDate' | 'dueDate' | 'gradesReleaseDate';

const DATE_FIELDS: { key: DateFieldKey; label: string }[] = [
  { key: 'openDate', label: 'Open date' },
  { key: 'dueDate', label: 'Due date' },
  { key: 'gradesReleaseDate', label: 'Grades release date' },
];

type FormState = {
  title: string;
  description: string;
  openDate: string;
  dueDate: string;
  gradesReleaseDate: string;
  maxGrade: string;
};

const initForm = (assignment?: Assignment): FormState => ({
  title: assignment?.title || '',
  description: assignment?.description || '',
  openDate: assignment?.startDate || '',
  dueDate: assignment?.endDate || '',
  gradesReleaseDate: assignment?.gradesReleasedAt || '',
  maxGrade: assignment?.maxGrade?.toString() || '',
});

const formatPayload = (
  assignment: Assignment,
  form: FormState
): AssignmentLaunchDetails => ({
  title: form.title,
  description: form.description || '',
  startDate: form.openDate,
  endDate: form.dueDate,
  gradesReleasedAt: form.gradesReleaseDate,
  maxGrade: parseInt(form.maxGrade, 10),
  providerId: assignment.providerId || '',
  resourceLinkId: assignment.resourceLinkId || '',
});

const InsperaModal = ({
  isVisible,
  onClose,
  assignment,
  currentSpace,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useDispatch();
  const maxGradeDisabled = assignment?.hasGradedSubmissions;

  const [form, setForm] = useState<FormState>(() => initForm(assignment));

  useEffect(() => {
    if (isVisible) setForm(initForm(assignment));
  }, [assignment, isVisible]);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState, boolean>>
  >({});
  const [openDateField, setOpenDateField] = useState<DateFieldKey | null>(null);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, boolean>> = {
      title: !form.title || form.title.length < 1,
      openDate: !form.openDate,
      dueDate: !form.dueDate,
      maxGrade: !form.maxGrade || isNaN(Number(form.maxGrade)),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleFieldChange =
    (name: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setErrors((prev) => ({ ...prev, [name]: false }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    };

  // Date-only comparison: ignore the time component entirely
  const dateOnly = (value?: string | Date) => {
    if (!value) return undefined;
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const handleDateConfirm = (key: DateFieldKey, date: Date) => {
    const normalized = new Date(date);
    normalized.setSeconds(0, 0);
    if (key === 'dueDate') {
      const openDay = dateOnly(form.openDate);
      if (openDay && dateOnly(normalized)! < openDay) {
        setPendingDate(null);
        setOpenDateField(null);
        return;
      }
    }
    if (key === 'gradesReleaseDate') {
      const earlierDay = dateOnly(form.dueDate) || dateOnly(form.openDate);
      if (earlierDay && dateOnly(normalized)! < earlierDay) {
        setPendingDate(null);
        setPendingDate(null);
        setOpenDateField(null);
        return;
      }
    }
    setErrors((prev) => ({ ...prev, [key]: false }));
    setForm((prev) => ({ ...prev, [key]: normalized.toISOString() }));
    setPendingDate(null);
    setOpenDateField(null);
  };

  const handleSave = async () => {
    if (!validate() || !assignment) {
      return;
    }
    setSaving(true);
    try {
      await updateLti1p3Assignment(
        currentSpace.objectId,
        assignment.objectId,
        formatPayload(assignment, form)
      );
      setSuccess(true);
    } catch {
      dispatch(
        showToast({
          emoji: '🙈',
          message: 'Oops! Something went wrong. Try again later.',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSaving(false);
    setSuccess(false);
    setErrors({});
    setForm(initForm(assignment));
    onClose();
  };

  return (
    <Modal open={isVisible} onClose={handleClose} data-testid="inspera-modal">
      <Box sx={styles.container}>
        {success ? (
          <AssignmentSuccessView
            title="Excellent!"
            showViewAssignmentButton={false}
            onViewAssignment={handleClose}
            onClose={handleClose}
            styles={styles}
          />
        ) : (
          <>
            {/* Header */}
            <Box sx={styles.header}>
              <Typography component="h2" sx={styles.title}>
                Edit your assignment
              </Typography>
              <IconButton
                aria-label="Close edit assignment dialog"
                onClick={handleClose}
                sx={styles.closeButton}
              >
                <Tooltip
                  title="Close"
                  disableHoverListener={!isDesktop}
                  disableFocusListener={!isDesktop}
                  disableTouchListener
                >
                  <Icons.Close />
                </Tooltip>
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={styles.content}>
              {/* Info banner */}
              <Box sx={styles.infoBanner}>
                <Icons.InfoOutlined sx={styles.infoIcon} />
                <Typography sx={styles.infoText}>
                  Changes made here may not also update in the assignment tool.
                  Please check to ensure alignment.
                </Typography>
              </Box>

              {/* Title */}
              <TextField
                label="Title"
                aria-label="Assignment title"
                value={form.title}
                sx={{ m: '0px', mb: errors.title ? '0.5rem' : '0px' }}
                onChange={handleFieldChange('title')}
                errorText={errors.title ? 'Title is required' : undefined}
              />

              {/* Description */}
              <TextField
                label="Description"
                kind="multiline"
                maxRows={3}
                value={form.description}
                onChange={handleFieldChange('description')}
                sx={{ m: '0px' }}
              />
              {/* Date fields */}
              {DATE_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  value={form[key] ? moment(form[key]).format('lll') : ''}
                  sx={{
                    cursor: 'pointer',
                    m: '0px',
                    mb: errors[key] ? '0.5rem' : '0px',
                  }}
                  errorText={errors[key] ? `${label} is required` : undefined}
                  inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                  onClick={() => {
                    setPendingDate(
                      form[key] ? new Date(form[key]) : new Date()
                    );
                    setOpenDateField(key);
                  }}
                />
              ))}

              {/* Max grade */}
              <TextField
                label="Maximum grade"
                value={form.maxGrade}
                disabled={maxGradeDisabled}
                onChange={handleFieldChange('maxGrade')}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (maxGradeDisabled) {
                    e.preventDefault();
                    return;
                  }
                  if (
                    !/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(
                      e.key
                    )
                  )
                    e.preventDefault();
                }}
                sx={{ m: '0px', mb: errors.maxGrade ? '0.5rem' : '0px' }}
                errorText={
                  errors.maxGrade ? 'Maximum grade is required' : undefined
                }
                helperText={
                  maxGradeDisabled
                    ? 'The max grade is not editable once graded submissions have been received'
                    : 'This should be a numerical value which matches the maximum grade in the assignment tool, e.g. 100.'
                }
                inputProps={{
                  disabled: maxGradeDisabled,
                  style: { cursor: maxGradeDisabled ? 'not-allowed' : 'text' },
                }}
              />
            </Box>

            {/* Footer */}
            <Box sx={styles.footer}>
              <div></div>
              <Button
                kind="primary"
                onClick={handleSave}
                loading={saving}
                sx={styles.buttonStyle}
                aria-label="Save"
              >
                Save
              </Button>
            </Box>

            {/* Date picker modal */}
            <Modal
              open={!!openDateField}
              onClose={() => setOpenDateField(null)}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  boxShadow: 24,
                  width: 320,
                  maxWidth: '95vw',
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{ bgcolor: 'background.paper', px: 2, pt: 1.5, pb: 0 }}
                >
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '12px',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Select date &amp; time
                  </Typography>
                </Box>
                <DateTimePicker
                  key={openDateField}
                  value={pendingDate || new Date()}
                  minDate={
                    openDateField === 'dueDate'
                      ? dateOnly(form.openDate)
                      : openDateField === 'gradesReleaseDate'
                      ? dateOnly(form.dueDate) || dateOnly(form.openDate)
                      : undefined
                  }
                  maxDate={
                    openDateField === 'openDate'
                      ? dateOnly(form.dueDate)
                      : openDateField === 'dueDate'
                      ? dateOnly(form.gradesReleaseDate)
                      : undefined
                  }
                  onChange={(date: any) => {
                    const d =
                      date instanceof Date ? date : date?.toDate?.() ?? null;
                    if (d) setPendingDate(d);
                  }}
                  sx={{
                    width: '100%',
                    boxShadow: 'none',
                    '& .MuiPickersDay-root.Mui-selected:hover': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    px: 2,
                    pb: 1.5,
                    pt: 0.5,
                  }}
                >
                  <Button
                    kind="secondary"
                    onClick={() => setOpenDateField(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    kind="secondary"
                    onClick={() => {
                      if (openDateField) {
                        handleDateConfirm(
                          openDateField,
                          pendingDate ?? new Date()
                        );
                      }
                    }}
                  >
                    Confirm
                  </Button>
                </Box>
              </Box>
            </Modal>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default InsperaModal;
